/**
 * DriverLogin - Componente de Login de Motorista
 * Autenticação isolada para motoristas (role: 'driver')
 * Design: Paleta Watch (verde gradient)
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
    Loader2,
    Mail,
    Lock,
    Eye,
    EyeOff,
    AlertCircle
} from 'lucide-react';

// Schema de validação do login
const loginSchema = z.object({
    email: z.string().email('Email inválido'),
    password: z.string().min(1, 'Senha é obrigatória')
});

type LoginFormData = z.infer<typeof loginSchema>;

export function DriverLogin() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema)
    });

    const onSubmit = async (data: LoginFormData) => {
        try {
            setIsLoading(true);
            setLoginError(null);

            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password
            });

            if (authError) {
                console.error('[DriverLogin] Erro no login:', authError);

                if (authError.message.includes('Invalid login')) {
                    setLoginError('Email ou senha incorretos');
                } else if (authError.message.includes('Email not confirmed')) {
                    setLoginError('Email não confirmado. Verifique sua caixa de entrada.');
                } else {
                    setLoginError(authError.message);
                }
                return;
            }

            if (!authData.user) {
                setLoginError('Erro ao fazer login');
                return;
            }

            const role = authData.user.user_metadata?.role;

            if (role !== 'driver') {
                await supabase.auth.signOut();
                setLoginError('Esta área é exclusiva para motoristas. Se você é cliente, use o login principal.');
                return;
            }

            const { data: driverData, error: driverError } = await supabase
                .from('drivers')
                .select('profile_status, status')
                .eq('id', authData.user.id)
                .single();

            if (driverError) {
                console.error('[DriverLogin] Erro ao buscar driver:', driverError);
                if (driverError.code === 'PGRST116') {
                    toast.info('Complete seu cadastro para continuar');
                    navigate('/driver/register');
                    return;
                }
                setLoginError('Erro ao carregar perfil');
                return;
            }

            if (driverData.status === 'suspended') {
                await supabase.auth.signOut();
                setLoginError('Sua conta está suspensa. Entre em contato com o suporte.');
                return;
            }

            toast.success('Login realizado com sucesso!');

            // Redirecionar baseado no profile_status (3 estágios)
            const profileStatus = driverData.profile_status;

            if (profileStatus === 'pending_submission') {
                navigate('/driver/onboarding');
            } else if (profileStatus === 'pending_review') {
                navigate('/driver/review');
            } else {
                // approved
                navigate('/dash_companion');
            }

        } catch (error) {
            console.error('[DriverLogin] Erro inesperado:', error);
            setLoginError('Erro inesperado. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-green-500 to-teal-600 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo Watch */}
                <div className="text-center mb-8">
                    <img
                        src="https://locaninja.com/logotipo-watch-white.png"
                        alt="Watch Logo"
                        className="h-16 mx-auto mb-6"
                    />
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Área do Motorista
                    </h1>
                    <p className="text-white/80">
                        Entre com suas credenciais
                    </p>
                </div>

                {/* Formulário */}
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="bg-white/95 backdrop-blur-lg rounded-2xl p-8 shadow-2xl"
                >
                    {/* Erro de Login */}
                    {loginError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-600 text-sm">{loginError}</p>
                        </div>
                    )}

                    {/* Campo: Email */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                {...register('email')}
                                placeholder="seu@email.com"
                                className={`w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.email
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                        </div>
                        {errors.email && (
                            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                        )}
                    </div>

                    {/* Campo: Senha */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Senha
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                {...register('password')}
                                placeholder="Sua senha"
                                className={`w-full pl-11 pr-12 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${errors.password
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-200 focus:ring-green-200 focus:border-green-400'
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        {errors.password && (
                            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                        )}
                    </div>

                    {/* Link Esqueci Senha */}
                    <div className="mb-6 text-right">
                        <Link
                            to="/driver/forgot-password"
                            className="text-sm text-green-600 hover:text-green-700 transition-colors"
                        >
                            Esqueceu a senha?
                        </Link>
                    </div>

                    {/* Botão de Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${isLoading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-green-500/30 hover:shadow-green-500/50'
                            }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Entrando...
                            </>
                        ) : (
                            'Entrar'
                        )}
                    </button>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-400">ou</span>
                        </div>
                    </div>

                    {/* Link para Cadastro */}
                    <p className="text-center text-gray-600">
                        Não tem uma conta?{' '}
                        <Link
                            to="/driver/register"
                            className="text-green-600 hover:text-green-700 font-medium transition-colors"
                        >
                            Cadastre-se
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

export default DriverLogin;
